import React, { createContext, useCallback, useContext, useReducer, useState } from 'react';

const CanvasContext = createContext();

const DEFAULT_STROKE_COLOR = '#38bdf8';
const DEFAULT_FILL_COLOR = 'transparent';
const DEFAULT_STROKE_WIDTH = 3;
const DEFAULT_OPACITY = 100;
const UNDO_LIMIT = 50;

// Fields that, when mutated, mark the session as dirty (unsaved)
const DIRTY_ACTIONS = new Set([
  'SET_CANVAS_METADATA', 'PUSH_UNDO', 'UPDATE_CANVAS',
  'ADD_LAYER', 'REMOVE_LAYER', 'RENAME_LAYER',
  'TOGGLE_LAYER_VISIBILITY', 'TOGGLE_LAYER_LOCK', 'REORDER_LAYERS',
  'SET_CURRENT_SNAPSHOT',
]);

function canvasReducer(state, action) {
  switch (action.type) {
    case 'SET_TOOL':
      return { ...state, activeTool: action.tool };

    case 'SET_STROKE_COLOR':
      return { ...state, strokeColor: action.color };

    case 'SET_FILL_COLOR':
      return { ...state, fillColor: action.color };

    case 'SET_STROKE_WIDTH':
      return { ...state, strokeWidth: action.width };

    case 'SET_OPACITY':
      return { ...state, opacity: action.opacity };

    case 'SET_FONT_SIZE':
      return { ...state, fontSize: action.size };

    case 'SET_ZOOM':
      return { ...state, zoom: action.zoom };

    case 'SET_CANVAS_METADATA':
      return { ...state, ...action.updates };

    case 'PUSH_UNDO': {
      const undoStack = [action.snapshot, ...state.undoStack].slice(0, UNDO_LIMIT);
      return { ...state, undoStack, redoStack: [] };
    }

    case 'UNDO': {
      if (state.undoStack.length === 0) return state;
      const [top, ...rest] = state.undoStack;
      return {
        ...state,
        undoStack: rest,
        redoStack: [state.currentSnapshot, ...state.redoStack],
        currentSnapshot: top,
        _pendingRestore: top,
      };
    }

    case 'REDO': {
      if (state.redoStack.length === 0) return state;
      const [top, ...rest] = state.redoStack;
      return {
        ...state,
        redoStack: rest,
        undoStack: [state.currentSnapshot, ...state.undoStack],
        currentSnapshot: top,
        _pendingRestore: top,
      };
    }

    case 'CLEAR_PENDING_RESTORE':
      return { ...state, _pendingRestore: null };

    case 'SET_CURRENT_SNAPSHOT':
      return { ...state, currentSnapshot: action.snapshot };

    case 'ADD_LAYER':
      return {
        ...state,
        layers: [
          ...state.layers,
          { id: action.id, name: action.name, visible: true, locked: false }
        ],
        activeLayerId: action.id,
      };

    case 'REMOVE_LAYER': {
      if (state.layers.length <= 1) return state;
      const newLayers = state.layers.filter((l) => l.id !== action.id);
      return {
        ...state,
        layers: newLayers,
        activeLayerId: state.activeLayerId === action.id ? newLayers[0].id : state.activeLayerId,
      };
    }

    case 'RENAME_LAYER':
      return {
        ...state,
        layers: state.layers.map((l) =>
          l.id === action.id ? { ...l, name: action.name } : l
        ),
      };

    case 'TOGGLE_LAYER_VISIBILITY':
      return {
        ...state,
        layers: state.layers.map((l) =>
          l.id === action.id ? { ...l, visible: !l.visible } : l
        ),
      };

    case 'TOGGLE_LAYER_LOCK':
      return {
        ...state,
        layers: state.layers.map((l) =>
          l.id === action.id ? { ...l, locked: !l.locked } : l
        ),
      };

    case 'REORDER_LAYERS':
      return { ...state, layers: action.layers };

    case 'SET_ACTIVE_LAYER':
      return { ...state, activeLayerId: action.id };

    // Bulk-restore from a saved session (autosave recovery)
    case 'RESTORE_SESSION':
      return {
        ...state,
        canvasNotes: action.session.canvasNotes ?? state.canvasNotes,
        roomMetadata: action.session.roomMetadata ?? state.roomMetadata,
        gridVisible: action.session.gridVisible ?? state.gridVisible,
        layers: action.session.layers ?? state.layers,
        activeLayerId: action.session.activeLayerId ?? state.activeLayerId,
      };

    // Legacy updateCanvas shim — merges arbitrary object
    case 'UPDATE_CANVAS':
      return { ...state, ...action.updates };

    default:
      return state;
  }
}

const initialState = {
  // Tool selection
  activeTool: 'select',
  availableTools: ['select', 'pan', 'pencil', 'rect', 'ellipse', 'line', 'arrow', 'text', 'image', 'eraser', 'eyedropper'],

  // Brush / style
  strokeColor: DEFAULT_STROKE_COLOR,
  fillColor: DEFAULT_FILL_COLOR,
  strokeWidth: DEFAULT_STROKE_WIDTH,
  opacity: DEFAULT_OPACITY,
  fontSize: 18,

  // Zoom
  zoom: 1,

  // Undo / redo
  undoStack: [],
  redoStack: [],
  currentSnapshot: null,
  _pendingRestore: null,

  // Layers
  layers: [{ id: 'layer-1', name: 'Background', visible: true, locked: false }],
  activeLayerId: 'layer-1',

  // Session metadata (legacy-compatible)
  roomMetadata: {
    roomId: process.env.REACT_APP_MATRIX_COLLAB_ROOM_ALIAS || '#togeevision-ops-collab-v2:togeevision.com',
    syncMode: 'REST_ONLY',
    lastSyncTimestamp: Date.now(),
    isProvisioned: true,
    localStateHash: 'sha256-canvas-initial-v1',
    matrixRoomAlias: process.env.REACT_APP_MATRIX_COLLAB_ROOM_ALIAS || '#togeevision-ops-collab-v2:togeevision.com',
  },
  gridVisible: true,
  canvasNotes: 'Drop reference imagery, map flows, and mark next moves.',
  tools: ['draw', 'select', 'text', 'erase'],
};

export function CanvasProvider({ children }) {
  const [canvasState, dispatch] = useReducer(canvasReducer, initialState);
  const [isDirty, setIsDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const dispatchCanvas = useCallback((action) => {
    dispatch(action);
    if (DIRTY_ACTIONS.has(action.type)) setIsDirty(true);
  }, []);

  // Legacy shim so existing code using updateCanvas({ ... }) still works
  const updateCanvas = useCallback((updates) => {
    dispatch({ type: 'UPDATE_CANVAS', updates });
    setIsDirty(true);
  }, []);

  // Called by useCanvasAutosave when a save completes
  const markSaved = useCallback((savedAt) => {
    setIsDirty(false);
    setLastSavedAt(savedAt ?? new Date());
  }, []);

  return (
    <CanvasContext.Provider value={{ canvasState, updateCanvas, dispatchCanvas, isDirty, lastSavedAt, markSaved }}>
      {children}
    </CanvasContext.Provider>
  );
}

export function useCanvas() {
  const context = useContext(CanvasContext);
  if (!context) {
    throw new Error('useCanvas must be used within a CanvasProvider');
  }
  return context;
}

export default CanvasProvider;
